import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutGrid, Users, QrCode, Send, Inbox, MessageSquare, AlertTriangle, Edit3, ExternalLink,
  CalendarDays, KeyRound, BookOpen, ShieldCheck, LogOut, Menu, X, ChevronRight, DatabaseBackup, Award } from "lucide-react";

export const EVENT_VIEWS = [
  { group: "Manage", items: [
    { key: "spreadsheet", label: "Roster", icon: LayoutGrid, testId: "view-spreadsheet-btn" },
    { key: "volunteers", label: "Registrant profiles", icon: Users, testId: "view-volunteers-btn" },
    { key: "signon", label: "Sign-on", icon: QrCode, testId: "signon-page-btn", path: (id) => `/admin/events/${id}/sign-on` },
    { key: "awards", label: "Volunteer awards", icon: Award, testId: "awards-page-btn", path: (id) => `/admin/events/${id}/awards` },
  ] },
  { group: "Communicate", items: [
    { key: "broadcast", label: "Broadcast", icon: Send, testId: "view-broadcast-btn" },
    { key: "emails", label: "Email inbox", icon: Inbox, testId: "view-emails-btn", badge: "emails" },
    { key: "sms", label: "SMS log", icon: MessageSquare, testId: "view-sms-btn", badge: "sms" },
    { key: "bounces", label: "Bounces", icon: AlertTriangle, testId: "view-bounces-btn", badge: "bounces", alert: true },
  ] },
  { group: "Event", items: [
    { key: "edit", label: "Edit event", icon: Edit3, testId: "edit-event-btn", path: (id) => `/admin/events/${id}/edit` },
  ] },
];

const itemCls = (active) =>
  `group flex w-full items-center gap-2.5 border-l-2 px-4 py-2 text-left text-sm transition-colors ${
    active ? "border-[#FF3B30] bg-[#F4F4F5] font-semibold text-black" : "border-transparent text-zinc-600 hover:bg-[#F4F4F5] hover:text-black"
  }`;

function Badge({ n, alert }) {
  if (!n) return null;
  return <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white ${alert ? "bg-red-600" : "bg-black/70"}`}>{n}</span>;
}

function EventNav({ event, active, onSelectView, badges, onNavigate }) {
  return (
    <div className="border-b border-black/10 pb-3" data-testid="sidebar-event-nav">
      <div className="px-4 pb-2 pt-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#002FA7]">Event</div>
        <div className="mt-0.5 line-clamp-2 font-display text-base font-black leading-tight tracking-tighter" data-testid="sidebar-event-name">{event.name || "…"}</div>
        {event.status ? <StatusPill status={event.status} /> : null}
      </div>
      {EVENT_VIEWS.map((g) => (
        <div key={g.group} className="mt-2">
          <div className="px-4 py-1 text-[10px] font-medium uppercase tracking-widest text-zinc-400">{g.group}</div>
          {g.items.map((it) => {
            const Icon = it.icon;
            const content = (<><Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{it.label}</span> {it.badge ? <Badge n={badges?.[it.badge]} alert={it.alert} /> : null}</>);
            if (!it.path && onSelectView) {
              return <button key={it.key} onClick={() => { onSelectView(it.key); onNavigate(); }} data-testid={it.testId} className={itemCls(active === it.key)}>{content}</button>;
            }
            const to = it.path ? it.path(event.id) : `/admin/events/${event.id}?view=${it.key}`;
            return <Link key={it.key} to={to} onClick={onNavigate} data-testid={it.testId} className={itemCls(active === it.key)}>{content}</Link>;
          })}
        </div>
      ))}
      {event.status === "published" && event.slug ? (
        <a href={`/events/${event.slug}`} target="_blank" rel="noreferrer" data-testid="sidebar-public-link" className={itemCls(false)}>
          <ExternalLink className="h-4 w-4" /> Public page
        </a>
      ) : null}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    published: "border-emerald-300 bg-emerald-50 text-emerald-800",
    archived: "border-amber-400 bg-amber-50 text-amber-800",
    draft: "border-zinc-300 bg-zinc-50 text-zinc-700",
  };
  return <span className={`mt-1.5 inline-flex border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${map[status] || map.draft}`} data-testid="event-status-pill">{status}</span>;
}

function GlobalNav({ user, onNavigate }) {
  const g = ({ isActive }) => itemCls(isActive);
  return (
    <div className="py-3" data-testid="sidebar-global-nav">
      <div className="px-4 py-1 text-[10px] font-medium uppercase tracking-widest text-zinc-400">Organiser</div>
      <NavLink end to="/admin" onClick={onNavigate} className={g} data-testid="sidebar-events-link"><CalendarDays className="h-4 w-4" /> All events</NavLink>
      <NavLink to="/admin/backups" onClick={onNavigate} className={g} data-testid="backups-link"><DatabaseBackup className="h-4 w-4" /> Backups</NavLink>
      <NavLink to="/admin/account" onClick={onNavigate} className={g} data-testid="account-settings-link"><KeyRound className="h-4 w-4" /> Security</NavLink>
      <NavLink to="/admin/help" onClick={onNavigate} className={g} data-testid="admin-help-link"><BookOpen className="h-4 w-4" /> Manual</NavLink>
      {user?.is_super_admin ? (
        <NavLink to="/super-admin" onClick={onNavigate} className={g} data-testid="super-admin-btn"><ShieldCheck className="h-4 w-4 text-[#B8860B]" /> Super Admin</NavLink>
      ) : null}
    </div>
  );
}

// Shared admin frame: slim top bar + left sidebar. `event` enables the event section nav.
export function AdminShell({ event, active, onSelectView, badges, title, actions, banner, children, contentClassName = "" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-shell">
      {banner}
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-black/10 bg-white px-4" data-testid="admin-topbar">
        <button onClick={() => setOpen(!open)} className="p-1 lg:hidden" data-testid="sidebar-toggle" aria-label="Menu">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        <Link to="/admin" className="font-display text-xl font-black tracking-tighter" data-testid="topbar-logo">VOLMEE<span className="text-[#FF3B30]">.</span></Link>
        <nav className="hidden min-w-0 items-center gap-1 text-sm text-zinc-500 md:flex" data-testid="topbar-breadcrumb">
          <ChevronRight className="h-4 w-4 text-zinc-300" />
          <Link to="/admin" className="hover:text-black" data-testid="back-to-events">Events</Link>
          {event ? (<><ChevronRight className="h-4 w-4 text-zinc-300" /><Link to={`/admin/events/${event.id}`} className="max-w-[260px] truncate font-semibold text-black hover:underline" data-testid="event-title">{event.name || "…"}</Link></>) : null}
          {title ? (<><ChevronRight className="h-4 w-4 text-zinc-300" /><span className="truncate text-black" data-testid="topbar-page-title">{title}</span></>) : null}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {actions}
          <div className="hidden h-6 w-px bg-black/10 sm:block" />
          <span className="hidden max-w-[200px] truncate text-xs text-zinc-500 sm:inline" title={user?.email} data-testid="admin-user-block">{user?.email}</span>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" title="Log out" className="inline-flex items-center gap-1.5 border border-black/10 px-2.5 py-1.5 text-xs hover:border-black"><LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span></button>
        </div>
      </header>
      <div className="flex">
        {open ? <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={close} data-testid="sidebar-backdrop" /> : null}
        <aside
          data-testid="admin-sidebar"
          className={`fixed bottom-0 left-0 top-0 z-50 w-64 lg:top-14 lg:z-30 lg:w-60 shrink-0 overflow-y-auto border-r border-black/10 bg-white transition-transform lg:sticky lg:h-[calc(100vh-3.5rem)] lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex h-14 items-center justify-between border-b border-black/10 px-4 lg:hidden">
            <span className="font-display text-xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></span>
            <button onClick={close} data-testid="sidebar-close" aria-label="Close menu"><X className="h-5 w-5" /></button>
          </div>
          {event ? <EventNav event={event} active={active} onSelectView={onSelectView} badges={badges} onNavigate={close} /> : null}
          <GlobalNav user={user} onNavigate={close} />
        </aside>
        <div className={`min-w-0 flex-1 ${contentClassName}`}>{children}</div>
      </div>
    </div>
  );
}
